import {
  Modal,
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
  TextInput,
} from 'carbon-components-react';

import './daily-fee.styles.scss';
import CustomButton from '../../components/custom-button/custom-btn.component';
import {
  getQueryString,
  hasExpired,
  OPTIONS,
  useReducerSpread,
} from '../../utils/common.util';
import useSWR from 'swr';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { postMethod } from '../../api/common';
import { useDebounceState } from '../../utils/useDebounceState';
import Combobox from '../../components/combobox/Combobox';

const initialData = {
  amountPaid: '',
  debitAccountId: '',
  paymentDate: '',
  description: '',
  paymentMode: '',
  receievedFrom: '',
  studentId: '',
  dailyBillingFeeId: '',
};

export default function MakeAdvancePayment({ isOpen, closeModal, onSuccess }) {
  const [formData, setFormData] = useReducerSpread(initialData);

  const { data: ledgerAccounts } = useSWR('chart-of-accounts/ledger-accounts');

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: s, isLoading: isStudentsLoading } = useSWR(
    `daily-billing/fees/assigned-students?${getQueryString({
      query: searchStudent,
      dailyBillingFeeId: formData.dailyBillingFeeId,
    })}`,
  );
  const students = s?.response ?? [];

  const { data } = useSWR(`daily-billing/fees/list`);
  const feeTypes = data?.dailyBillingFee ?? [];

  const { trigger, isMutating, error, reset } = useCustomMutation(
    `daily-billing/revenue/create-advance-payment`,
    (url, { arg }) => postMethod(url, arg),
  );

  function handleSubmit(e) {
    e.preventDefault();
    trigger(formData, {
      onSuccess: (data) => {
        onSuccess?.(data?.data?.payment);
        handleClose();
      },
    });
  }

  function handleClose() {
    setFormData(initialData);
    reset();
    closeModal();
  }

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  return (
    <Modal
      id="create-advance-modal"
      className="create-advance-modal"
      hasScrollingContent
      passiveModal={true}
      open={isOpen}
      modalHeading={`Make Deposit Payment`}
      primaryButtonDisabled={false}
    >
      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: 'grid',
            gap: '20px',
            gridTemplateColumns: 'repeat(2, 1fr)',
          }}
        >
          <Select
            labelText="Fee Type"
            value={formData.dailyBillingFeeId}
            onChange={(e) =>
              setFormData({ dailyBillingFeeId: e.target.value, studentId: '' })
            }
          >
            <SelectItem selected value="" text="" />
            {feeTypes?.map((item) => (
              <SelectItem
                key={item.id}
                text={
                  item.name +
                  (isDisabled(item) && item?.invoice?.status
                    ? ` - [${item.invoice.status}]`
                    : '')
                }
                value={item.id}
                disabled={isDisabled(item)}
              />
            ))}
          </Select>
          <Combobox
            options={students ?? []}
            label="Student"
            disabled={!formData.dailyBillingFeeId}
            value={
              formData.studentId
                ? students?.find((s) => s.id === formData.studentId)
                : null
            }
            onChange={(option) => {
              setFormData({ studentId: option ? option.id : '' });
              setSearchStudent('');
            }}
            onSearch={setSearchStudent} // The combobox now just updates the search term state
            onClear={() => {
              setFormData({ studentId: '' });
              setSearchStudent('');
            }}
            loading={isStudentsLoading} // Pass the loading state down
            renderSelected={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            renderOption={(option) => `${option.firstName} ${option.lastName}`}
            labelKey="fullName"
            valueKey="id"
            searchPlaceholder="Search students..."
            placeholder=""
          />

          <TextInput
            name="amount"
            labelText="Amount"
            placeholder="Enter Amount"
            type={'number'}
            value={formData.amountPaid}
            onChange={(e) => {
              setFormData({ amountPaid: Number(e.target.value) });
            }}
          />

          <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            id="advance-date-picker"
            maxDate={new Date().toISOString().split('T')[0]}
            onChange={(value) => {
              if (value.length > 0) {
                setFormData({
                  paymentDate: [
                    value[0].getFullYear(),
                    ('0' + (value[0].getMonth() + 1)).slice(-2),
                    ('0' + value[0].getDate()).slice(-2),
                  ].join('-'),
                });
              } else setFormData({ paymentDate: '' });
            }}
            onClose={(value) => {}}
          >
            <DatePickerInput
              className="payment-date"
              id="payment-date"
              name="paymentDate"
              labelText="Payment Date"
              value={formData.paymentDate}
              max={new Date().toISOString().split('T')[0]}
              // pattern="d{1,2}/d{4}"
              pattern="\d{4}-\d{2}-\d{2}"
              autoComplete="off"
              placeholder="YYYY-MM-DD"
              type="text"
              onChange={(e) => setFormData({ paymentDate: e.target.value })}
            />
          </DatePicker>

          <Select
            id="debitAccount"
            labelText="Debit Account (Cash or Bank Account)"
            value={formData.debitAccountId}
            onChange={(e) => setFormData({ debitAccountId: e.target.value })}
          >
            <SelectItem selected text="" value="" />
            {ledgerAccounts
              ?.filter(
                (currentAsset) =>
                  currentAsset.categoryDescription === 'Bank Accounts' ||
                  currentAsset.categoryDescription === 'Cash Accounts',
              )
              .map((currentAsset) => (
                <SelectItem
                  key={currentAsset.id}
                  text={currentAsset.description}
                  value={currentAsset.id}
                />
              ))}
          </Select>

          <Select
            labelText="Payment Mode"
            value={formData.paymentMode}
            onChange={(e) => setFormData({ paymentMode: e.target.value })}
          >
            <SelectItem selected text="" value="" />
            {OPTIONS.paymentModes
              .filter((mode) => mode.value !== 'Cheque')
              .map((mode) => (
                <SelectItem
                  key={mode.value}
                  value={mode.value}
                  text={mode.label}
                />
              ))}
          </Select>

          <TextInput
            id="receivedFrom"
            name="receivedFrom"
            labelText="Received From"
            placeholder="Enter who you received the money from"
            value={formData?.receievedFrom}
            onChange={(e) => setFormData({ receievedFrom: e.target.value })}
          />

          <TextInput
            name="description"
            labelText="Description"
            placeholder="Enter  description"
            value={formData?.description}
            onChange={(e) => setFormData({ description: e.target.value })}
          />
        </div>

        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}

        <div
          style={{
            marginTop: '30px',
            display: 'flex',
            gap: '20px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <CustomButton
            classes="outline danger no-hover font-bold"
            style={{ flex: '1' }}
            onClick={handleClose}
          >
            CLOSE
          </CustomButton>

          <CustomButton
            type="submit"
            style={{ flex: '1' }}
            classes={`primary font-bold`}
            isLoading={isMutating}
            disabled={
              !formData.amountPaid ||
              !formData.debitAccountId ||
              !formData.paymentDate ||
              !formData.paymentMode ||
              !formData.receievedFrom ||
              !formData.studentId ||
              !formData.dailyBillingFeeId
            }
          >
            Submit Payment
          </CustomButton>
        </div>
      </form>
    </Modal>
  );
}
